import { useKakaoMap } from '@/hooks/useKakaoMap';
import { useSearchPositionMutation } from '@/remote/geo/search-position';
import { useState } from 'react';

interface AddressData {
  postcode?: string;
  address?: string;
  detailAddress?: string;
  extraAddress?: string;
  latitude?: number;
  longitude?: number;
}

interface AddressInputProps {
  label?: string;
  onChange: (
    fullAddress: string,
    coordinates?: { latitude: number; longitude: number }
  ) => void;
  className?: string;
  isMapOpen?: boolean;
  initialMapPosition?: {
    latitude: number;
    longitude: number;
    address: string;
    name: string;
  };
}

// 서울시청 기본 좌표 상수
const DEFAULT_SEOUL_COORDINATES = {
  latitude: 37.5642135,
  longitude: 127.0016985,
} as const;

export function AddressInput({
  label = '주소',
  onChange,
  className = '',
  isMapOpen: initialIsMapOpen = false,
  initialMapPosition: initialMapPosition = {
    latitude: DEFAULT_SEOUL_COORDINATES.latitude,
    longitude: DEFAULT_SEOUL_COORDINATES.longitude,
    address: '',
    name: '',
  },
}: Readonly<AddressInputProps>) {
  const { mutateAsync: searchPosition } = useSearchPositionMutation();
  const [isMapOpen, setIsMapOpen] = useState(initialIsMapOpen);
  const [address, setAddress] = useState<AddressData>({
    address: initialMapPosition.address,
  });

  const [mapPosition, setMapPosition] = useState<{
    latitude: number;
    longitude: number;
    address: string;
  }>(initialMapPosition);

  const handleChange = async (newAddressData: AddressData) => {
    // 기존 상세주소를 유지하면서 새로운 주소 데이터 병합
    const mergedAddressData = {
      ...newAddressData,
      detailAddress: newAddressData.detailAddress || address.detailAddress,
    };

    setAddress(mergedAddressData);
    onChange(mergedAddressData.address ?? '', {
      latitude: mergedAddressData.latitude ?? initialMapPosition.latitude,
      longitude: mergedAddressData.longitude ?? initialMapPosition.longitude,
    });

    // 주소가 설정되면 자동으로 지도 열기
    if (
      mergedAddressData.latitude &&
      mergedAddressData.longitude &&
      !isMapOpen
    ) {
      setIsMapOpen(true);
    }
  };

  const handleSearchPosition = async (address: string) => {
    const result = await searchPosition({ address }).then((result) => {
      if (!result.success) {
        alert(
          '위도와 경도를 찾을 수 없습니다. 도로명 주소를 다시 확인해주세요.'
        );
      }
      return result;
    });

    if (result.success) {
      onChange(result.address, {
        latitude: result.latitude,
        longitude: result.longitude,
      });
      setMapPosition({
        latitude: result.latitude,
        longitude: result.longitude,
        address: result.address,
      });
    }
  };

  // 지도 클릭 핸들러
  const handleMapClick = (latitude: number, longitude: number) => {
    // 마커 위치 업데이트
    setMapPosition({
      latitude,
      longitude,
      address: address.address ?? '',
    });

    // 부모 컴포넌트에 좌표 전달
    onChange(address.address ?? '', {
      latitude,
      longitude,
    });
  };
  // 지도 옵션 - mapPosition이 변경될 때마다 재계산
  const mapOptions = {
    positions: [
      {
        latitude: mapPosition.latitude,
        longitude: mapPosition.longitude,
        name: mapPosition.address,
        address: mapPosition.address,
      },
    ],
    level: 3,
    center: {
      latitude: mapPosition.latitude,
      longitude: mapPosition.longitude,
      name: mapPosition.address,
    },
    onClick: handleMapClick,
  };

  // 지도 훅 사용
  const { mapRef } = useKakaoMap(isMapOpen, mapOptions);

  return (
    <div className={className}>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {label}
      </label>
      <div className="space-y-2">
        <div className="flex items-center justify-between gap-2">
          <input
            type="text"
            value={address.address}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
            placeholder="지번 또는 도로명 주소를 입력하세요 (예: 가마골길 1)"
            onChange={(e) => handleChange({ address: e.target.value })}
          />
          <button
            className="w-[100px] flex items-center justify-center  px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors "
            type="button"
            onClick={() => handleSearchPosition(address.address ?? '')}
            disabled={!address.address}
          >
            검색
          </button>
        </div>

        {isMapOpen && (
          <div className="mt-4 border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-3 py-2 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">
                  위치 지도
                </span>
                <span className="text-xs text-gray-500">
                  {address.address
                    ? '선택된 주소 위치'
                    : '기본 위치 (서울시청)'}
                </span>
              </div>
            </div>
            <div
              ref={mapRef}
              className="w-full h-64 bg-gray-100"
              style={{ minHeight: '256px' }}
            >
              {!isMapOpen && (
                <div className="flex items-center justify-center h-full text-gray-500">
                  지도를 로딩 중입니다...
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
